<div class="create-role-page">
  <!-- Page Header -->
  <div class="page-top">
    <div class="page-top__left">
      <div class="breadcrumb-row">
        <button type="button" class="back-chevron" (click)="cancel()" aria-label="Go back">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path fill-rule="evenodd"
              d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0" />
          </svg>
        </button>
        <div>
          <span class="breadcrumb-text">Administration / Roles &amp; Permissions / <strong>New role</strong></span>
          <h1 class="page-title">Create role</h1>
          <p class="page-subtitle">Define a custom role and configure its permission matrix</p>
        </div>
      </div>
    </div>
    <div class="page-top__actions">
      <button type="button" class="btn-ghost" (click)="cancel()">Cancel</button>
      <button type="button" class="btn-primary-teal" (click)="submit()">+ Create Role</button>
    </div>
  </div>

  <!-- Two-Column Layout -->
  <div class="two-col">
    <!-- Left Column: Form -->
    <div class="col-form">
      <form [formGroup]="form" (ngSubmit)="submit()" novalidate>

        <!-- Section 01: Identity -->
        <div class="form-card">
          <div class="section-header">
            <div class="section-header__left">
              <span class="section-num">01</span>
              <span class="section-title">Identity</span>
            </div>
            <span class="section-meta">Name &amp; description</span>
          </div>

          <div class="section-body">
            <!-- Role Name -->
            <div class="field-group">
              <div class="field-label-row">
                <label class="field-label">
                  Role name <span class="required-dot">●</span>
                </label>
                <span class="field-badge">must be unique</span>
              </div>
              <div class="name-input-wrap">
                <input type="text" class="field-input"
                  [class.field-input--error]="(submitted && form.get('name')?.invalid) || nameValidationState.status === 'conflict' || nameValidationState.status === 'error'"
                  [class.field-input--valid]="nameValidationState.status === 'valid'"
                  formControlName="name" placeholder="e.g. Region Coordinator" />

                @if (nameValidationState.status === 'checking') {
                  <span class="name-status-icon"><span class="name-spinner"></span></span>
                }
                @if (nameValidationState.status === 'valid') {
                  <span class="name-status-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#3DA66E" viewBox="0 0 16 16">
                      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                    </svg>
                  </span>
                }
                @if (nameValidationState.status === 'error') {
                  <span class="name-status-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#E53E3E" viewBox="0 0 16 16">
                      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M8 4a.905.905 0 0 0-.9.995l.35 3.507a.552.552 0 0 0 1.1 0l.35-3.507A.905.905 0 0 0 8 4m.002 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2" />
                    </svg>
                  </span>
                }
                @if (nameValidationState.status === 'conflict') {
                  <span class="name-status-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#DD6B20" viewBox="0 0 16 16">
                      <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5m.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2" />
                    </svg>
                  </span>
                }
              </div>

              @if (nameValidationState.status === 'idle') {
                <span class="field-helper">Letters, numbers, spaces, and hyphens. Must be unique.</span>
              }
              @if (nameValidationState.status === 'checking') {
                <span class="field-helper">Checking availability…</span>
              }
              @if (nameValidationState.status === 'valid') {
                <span class="field-valid-msg">✓ Available</span>
              }
              @if (nameValidationState.status === 'error') {
                <span class="field-error-msg">{{ nameValidationState.message }}</span>
              }
              @if (nameValidationState.status === 'conflict') {
                <span class="field-conflict-msg">{{ nameValidationState.message }}</span>
              }
            </div>

            <!-- Description -->
            <div class="field-group">
              <div class="field-label-row">
                <label class="field-label">Description</label>
                <span class="field-badge">optional</span>
              </div>
              <textarea class="field-textarea" formControlName="description" rows="2"
                placeholder="Briefly describe what this role does…"></textarea>
            </div>

            <!-- Start From Template (hidden when duplicating a role) -->
            @if (!hasInitialPermissions) {
              <div class="field-group field-group--last">
                <div class="field-label-row">
                  <label class="field-label">Start from template</label>
                  <span class="field-badge">optional</span>
                </div>
                <div class="template-pills">
                  @for (tpl of templates; track tpl) {
                    <button type="button" class="pill"
                      [class.pill--active]="selectedTemplate === tpl" (click)="selectTemplate(tpl)">
                      {{ tpl }}
                    </button>
                  }
                </div>
              </div>
            }
          </div>
        </div>

        <!-- Section 02: Permissions -->
        <div class="form-card">
          <div class="section-header">
            <div class="section-header__left">
              <span class="section-num">02</span>
              <span class="section-title">Permissions</span>
            </div>
            <span class="section-meta">{{ grantedCount }} of 23 granted across 7 modules</span>
          </div>

          <div class="section-body">
            <app-permission-selector
              [selectedTemplate]="selectedTemplate"
              [initialPermissions]="initialPermissions"
              (modulesChange)="onModulesChange($event)"
              (totalPermissionsCount)="onPermissionCountChange($event)"
              (moduleCountsChange)="onModuleCountsChange($event)">
            </app-permission-selector>
          </div>
        </div>

      </form>
    </div>

    <!-- Right Column: Sticky Sidebar -->
    <div class="col-sidebar">
      <div class="sidebar-sticky">

        <!-- Error Banner -->
        @if (submitted && getValidationIssues().length > 0) {
          <div class="error-banner">
            <div class="error-banner__header">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path
                  d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5m.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2" />
              </svg>
              <span>{{ getValidationIssues().length }} issue blocking save</span>
            </div>
            <div class="error-banner__body">
              @for (issue of getValidationIssues(); track issue) {
                <div class="error-banner__issue">
                  <span>&rarr;</span> <span>{{ issue }}</span>
                </div>
              }
            </div>
          </div>
        }

        <!-- Live Preview Card -->
        <div class="sidebar-card">
          <h6 class="sidebar-card__label">LIVE PREVIEW</h6>
          <div class="preview-box">
            <div class="preview-box__icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="currentColor" viewBox="0 0 16 16">
                <path
                  d="M5.338.56C6.408.26 7.3 0 8 0s1.592.26 2.662.56c1.11.3 2.229.655 2.887.87a1.54 1.54 0 0 1 1.044 1.262c.596 4.477-.787 7.791-2.483 10.007A11.7 11.7 0 0 1 9.593 15.152a7 7 0 0 1-1.094.638c-.3.13-.593.21-.844.21s-.543-.08-.844-.21a7 7 0 0 1-1.094-.638A11.7 11.7 0 0 1 3.2 12.699C1.507 10.483.124 7.169.72 2.692A1.54 1.54 0 0 1 1.764 1.43c.658-.215 1.777-.57 2.887-.87z" />
              </svg>
            </div>
            <div class="preview-box__info">
              <div class="preview-box__name">{{ form.get('name')?.value || 'Unnamed user' }}</div>
              <div class="preview-box__role">No role assigned</div>
            </div>
          </div>
        </div>

        <!-- Access by Module Summary -->
        <div class="sidebar-card">
          <div class="sidebar-card__header-row">
            <h6 class="sidebar-card__label">ACCESS BY MODULE</h6>
            <span class="counter-badge">{{ grantedCount }}/23</span>
          </div>
          <div class="module-list">
            @for (modName of MODULE_NAMES; track modName) {
              <div class="module-list__row">
                <span class="module-list__name">
                  @switch (modName) {
                    @case ('User Management') {
                      <img src="assets/icons/users.svg" alt="" width="14" height="14" />
                    }
                    @case ('Roles & Permissions') {
                      <img src="assets/icons/shield.svg" alt="" width="14" height="14" />
                    }
                    @case ('Geographic Nodes') {
                      <img src="assets/icons/pin.svg" alt="" width="14" height="14" />
                    }
                    @case ('Channels') {
                      <img src="assets/icons/check.svg" alt="" width="14" height="14" />
                    }
                    @case ('Dispatch & Operations') {
                      <img src="assets/icons/dispatch_&_operations.svg" alt="" width="14" height="14" />
                    }
                    @case ('Reports & Analytics') {
                      <img src="assets/icons/reports_&_analytics.svg" alt="" width="14" height="14" />
                    }
                    @case ('System Settings') {
                      <img src="assets/icons/settings.svg" alt="" width="14" height="14" />
                    }
                  }
                  {{ modName }}
                </span>
                @if (moduleCountsMap[modName]) {
                  <span class="module-list__status">
                    @if (moduleCountsMap[modName].granted === 0) { None } @else { {{ moduleCountsMap[modName].granted }}/{{ moduleCountsMap[modName].total }} }
                  </span>
                }
              </div>
            }
          </div>
        </div>

        <!-- Audit Info Box -->
        <div class="audit-box">
          <div class="audit-box__icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
              <path
                d="M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5m0 2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5" />
              <path
                d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5z" />
            </svg>
          </div>
          <p class="audit-box__text">
            <strong>Changes will be logged</strong>&ensp;Role creation and every subsequent permission change is
            recorded in the audit trail with timestamp, actor, and diff.
          </p>
        </div>

      </div>
    </div>
  </div>
</div>
